import { useMemo } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MultiPolygon } from "geojson";

import { ApiError, ApiResponse } from "../types/api";
import type { BoundaryStyle } from "../types/boundaryStyle";
import { api } from "../utils/api";

/** [west, south, east, north] in degrees. */
export type FolderBbox = [number, number, number, number];

/** One folder in the tenant-wide project tree; `parent` null = top level. */
export interface ProjectFolder {
  id: number;
  name: string;
  parent: number | null;
  /** Drawn on the listing map, with everything inside it (tenant-wide). */
  map_visible: boolean;
  /** Pinned to the top of its parent's folder list, for everyone. */
  is_pinned: boolean;
  pinned_at: string | null;
  /**
   * Extent of the folder's boundary layer (the estate / region it stands
   * for), or null when it has none. The geometry itself comes from
   * useFolderBoundaries, only for the folders the map is about to draw.
   */
  boundary_bbox: FolderBbox | null;
  /** Polygons that went into the boundary. */
  boundary_feature_count: number;
  /** The property-boundary data source it was pulled from, if any. */
  boundary_source: number | null;
  boundary_source_name: string | null;
  /** The file it was read from, if any (empty when from a source). */
  boundary_file_name: string;
  boundary_updated_at: string | null;
  /** How the boundary is drawn; null = the default amber. */
  boundary_style: BoundaryStyle | null;
  created_at: string;
  updated_at: string;
}

export interface DeleteFolderResult {
  deleted_id: number;
  /** Where the folder's contents went (null = top level). */
  parent: number | null;
  moved_folders: number;
  moved_projects: number;
  /** Folder-level story maps re-filed to the parent. */
  moved_story_maps: number;
}

/** GET /api/project-folders/<id>/boundary */
export interface FolderBoundary {
  id: number;
  name: string;
  geometry: MultiPolygon | null;
  bbox: FolderBbox | null;
  feature_count: number;
}

/** What POST /boundary reports about the polygons it kept. */
export interface SetFolderBoundaryOutcome {
  feature_count: number;
  skipped: number;
  truncated: boolean;
  /** The source was pulled over the folder's project extent, not whole. */
  clipped_to_projects: boolean;
  warnings: string[];
}

export type SetFolderBoundaryResult = ProjectFolder & { result: SetFolderBoundaryOutcome };

export type SetFolderBoundaryInput = { folderId: number } & (
  | { filestoreFileId: number }
  | { dataSourceId: number }
);

export const PROJECT_FOLDERS_KEY = ["project-folders"] as const;
export const folderBoundaryKey = (id: number) => ["project-folder-boundary", id] as const;

const EMPTY: ProjectFolder[] = [];
const EMPTY_BOUNDARIES: ReadonlyMap<number, FolderBoundary> = new Map();

/** The whole tree, flat. The client builds the hierarchy (see folderTree.ts). */
export const useProjectFolders = (enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: PROJECT_FOLDERS_KEY,
    queryFn: () => api.request<ApiResponse<ProjectFolder[]>>("/api/project-folders"),
    enabled,
    refetchOnWindowFocus: false,
  }) as {
    data: ApiResponse<ProjectFolder[]> | undefined;
    isLoading: boolean;
    error: ApiError | null;
  };
  return { folders: data?.data ?? EMPTY, isLoading, error };
};

/**
 * The boundary layers of `ids`, by folder id, as they load. Only folders with
 * a boundary (`boundary_bbox` set) are worth asking for; a folder without
 * one answers with a null geometry and is left out of the map. Cached for a
 * while: a boundary changes only through the dialog, which invalidates it.
 */
export const useFolderBoundaries = (ids: number[]): ReadonlyMap<number, FolderBoundary> => {
  const key = ids.join(",");
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: folderBoundaryKey(id),
      queryFn: () => api.request<ApiResponse<FolderBoundary>>(`/api/project-folders/${id}/boundary`),
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  });
  // A fresh Map only when something loaded or the set changed; consumers
  // memoise on it.
  const loadedKey = results.map((r) => (r.data?.data?.geometry ? "1" : "0")).join("");
  return useMemo(() => {
    if (ids.length === 0) return EMPTY_BOUNDARIES;
    const map = new Map<number, FolderBoundary>();
    results.forEach((r, i) => {
      const boundary = r.data?.data;
      if (boundary?.geometry) map.set(ids[i], boundary);
    });
    return map;
    // `key` and `loadedKey` stand in for the arrays' identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, loadedKey]);
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ApiResponse<ProjectFolder>,
    ApiError,
    { name: string; parent: number | null }
  >({
    mutationFn: (body) =>
      api.request<ApiResponse<ProjectFolder>>("/api/project-folders", {
        method: "POST",
        body,
      }),
    onSuccess: (res) => {
      // Into the list straight away: the listing swaps its "naming a new
      // folder" row for the real one, and a gap until the refetch would flicker.
      const created = res?.data;
      if (created) {
        queryClient.setQueryData<ApiResponse<ProjectFolder[]>>(PROJECT_FOLDERS_KEY, (prev) =>
          prev?.data && !prev.data.some((f) => f.id === created.id)
            ? { ...prev, data: [...prev.data, created] }
            : prev
        );
      }
      queryClient.invalidateQueries({ queryKey: PROJECT_FOLDERS_KEY });
    },
  });
};

/**
 * Rename and/or move a folder. Optimistic on the folder list so the row
 * re-labels or re-files instantly; the server stays authoritative on the
 * tree rules (cycles, depth, duplicate names) and a refusal rolls back.
 */
export const useUpdateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ApiResponse<ProjectFolder>,
    ApiError,
    {
      folderId: number;
      name?: string;
      parent?: number | null;
      map_visible?: boolean;
      is_pinned?: boolean;
      boundary_style?: BoundaryStyle | null;
    },
    { previous: ApiResponse<ProjectFolder[]> | undefined }
  >({
    mutationFn: ({ folderId, ...body }) =>
      api.request<ApiResponse<ProjectFolder>>(`/api/project-folders/${folderId}`, {
        method: "PATCH",
        body,
      }),
    onMutate: async ({ folderId, name, parent, map_visible, is_pinned, boundary_style }) => {
      await queryClient.cancelQueries({ queryKey: PROJECT_FOLDERS_KEY });
      const previous = queryClient.getQueryData<ApiResponse<ProjectFolder[]>>(PROJECT_FOLDERS_KEY);
      if (previous?.data) {
        queryClient.setQueryData(PROJECT_FOLDERS_KEY, {
          ...previous,
          data: previous.data.map((f) =>
            f.id === folderId
              ? {
                  ...f,
                  ...(name !== undefined ? { name } : {}),
                  ...(parent !== undefined ? { parent } : {}),
                  ...(map_visible !== undefined ? { map_visible } : {}),
                  // pinned_at null reads as "newest pin" until the refetch (see byPinThenName).
                  ...(is_pinned !== undefined ? { is_pinned, pinned_at: null } : {}),
                  ...(boundary_style !== undefined ? { boundary_style } : {}),
                }
              : f
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(PROJECT_FOLDERS_KEY, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_FOLDERS_KEY });
    },
  });
};

/** Delete a folder. Its sub-folders and projects move to its parent. */
export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<DeleteFolderResult>, ApiError, number>({
    mutationFn: (folderId) =>
      api.request<ApiResponse<DeleteFolderResult>>(`/api/project-folders/${folderId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_FOLDERS_KEY });
      // The server re-filed the folder's projects; both lists carry `folder`.
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects-archived"] });
    },
  });
};

/** Write the folder the server sent back into the list, then let it refetch. */
function settleFolder(queryClient: ReturnType<typeof useQueryClient>, folder: ProjectFolder | undefined) {
  if (folder) {
    queryClient.setQueryData<ApiResponse<ProjectFolder[]>>(PROJECT_FOLDERS_KEY, (prev) =>
      prev?.data ? { ...prev, data: prev.data.map((f) => (f.id === folder.id ? { ...f, ...folder } : f)) } : prev
    );
    queryClient.invalidateQueries({ queryKey: folderBoundaryKey(folder.id) });
  }
  queryClient.invalidateQueries({ queryKey: PROJECT_FOLDERS_KEY });
}

/**
 * Set a folder's boundary layer from a filestore file (zipped shapefile,
 * KML, GeoJSON) or from a property-boundary data source. The server parses
 * or pulls, keeps the polygons and answers with the folder plus a `result`
 * saying how many it kept.
 */
export const useSetFolderBoundary = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<SetFolderBoundaryResult>, ApiError, SetFolderBoundaryInput>({
    mutationFn: ({ folderId, ...source }) =>
      api.request<ApiResponse<SetFolderBoundaryResult>>(`/api/project-folders/${folderId}/boundary`, {
        method: "POST",
        body:
          "filestoreFileId" in source
            ? { filestore_file_id: source.filestoreFileId }
            : { data_source_id: source.dataSourceId },
      }),
    onSuccess: (res) => {
      if (res?.data) {
        // The folder as the list serialises it, minus the import summary.
        const { result, ...folder } = res.data;
        void result;
        settleFolder(queryClient, folder);
      } else {
        settleFolder(queryClient, undefined);
      }
    },
  });
};

/** Remove a folder's boundary layer. */
export const useClearFolderBoundary = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<ProjectFolder>, ApiError, number>({
    mutationFn: (folderId) =>
      api.request<ApiResponse<ProjectFolder>>(`/api/project-folders/${folderId}/boundary`, {
        method: "DELETE",
      }),
    onSuccess: (res) => settleFolder(queryClient, res?.data),
  });
};
